/**
 * GameTerms — single source of truth for all game term formatting.
 *
 * Every UI surface (shop, inventory, tooltips, combat log, party screen,
 * codex, crafting) MUST use these helpers. Never inline-construct game
 * term text in a screen-specific way.
 *
 * PoE players read stat sheets like legal documents. One mismatch and
 * they assume the system is broken.
 */

import { displayMomentum } from '../../utils/format';

// ── Capitalize helper (private) ────────────────────────────────

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

// ── Element formatting ─────────────────────────────────────────

/** Format an element ID for display: "fire" → "Fire" */
export function formatElement(element: string): string {
  return cap(element);
}

/** CSS class for element-colored text */
export function elementClass(element: string): string {
  return `element-${element}`;
}

/** Format a list of defensive types: ["fire", "water"] → "Fire / Water" */
export function formatDefensiveTypes(types: string[]): string {
  return types.map(cap).join(' / ');
}

/** Same as formatDefensiveTypes but each type is wrapped in its element-{name}
 *  class so it picks up the canonical element color. Use anywhere the type
 *  list is shown to the player (codex cards, detail pages, hero select). */
export function formatDefensiveTypesColored(types: string[]): string {
  return types
    .map(t => `<span class="${elementClass(t)}">${cap(t)}</span>`)
    .join(' / ');
}

// ── Ability formatting ─────────────────────────────────────────

export const TARGET_LABELS: Record<string, string> = {
  single_enemy: 'Single Enemy',
  all_enemies: 'All Enemies',
  single_ally: 'Single Ally',
  all_allies: 'All Allies',
  self: 'Self',
};

export const TYPE_LABELS: Record<string, string> = {
  damage: 'Damage',
  heal: 'Heal',
  cleanse: 'Cleanse',
  buff: 'Self-Buff',
  summon: 'Summon',
  trap: 'Trap',
};

/** Format ability target: "single_enemy" → "Single Enemy" */
export function formatTarget(target: string): string {
  return TARGET_LABELS[target] ?? target.replace(/_/g, ' ').split(' ').map(cap).join(' ');
}

/** Format ability type: "damage" → "Damage" */
export function formatAbilityType(type: string): string {
  return TYPE_LABELS[type] ?? cap(type);
}

/**
 * Target phrase for prose damage/heal lines.
 * Damage targets use "to an enemy / all enemies / adjacent enemies".
 * Heal/buff targets use "to an ally / all allies / self".
 */
const DAMAGE_TARGET_PHRASES: Record<string, string> = {
  single_enemy:     'to an enemy',
  all_enemies:      'to all enemies',
  adjacent_enemies: 'to adjacent enemies',
  single_ally:      'to an ally',
  all_allies:       'to all allies',
  self:             'to self',
};

/**
 * Build a prose damage/heal line for an ability tooltip.
 * Damage: "Deals 15-63 <Element>Fire</Element> damage to all enemies"
 * Heal:   "Restores 15-63 Health to an ally"
 * Self:   damage prose omits "to self" (caster-only auras read better silent)
 */
export function formatAbilityDamageLine(opts: {
  type: 'damage' | 'heal' | 'trap';
  element: string;
  target: string;
  min: number;
  max: number;
}): string {
  const targetPhrase = DAMAGE_TARGET_PHRASES[opts.target] ?? '';
  if (opts.type === 'heal') {
    const range = `<strong class="heal">${opts.min}-${opts.max}</strong>`;
    return `Restores ${range} Health ${targetPhrase}`.trimEnd();
  }
  const elClass = elementClass(opts.element);
  const elLabel = formatElement(opts.element);
  const range = `<strong class="${elClass}">${opts.min}-${opts.max}</strong> <span class="${elClass}">${elLabel}</span>`;
  const verb = opts.type === 'trap' ? 'Trap deals' : 'Deals';
  return `${verb} ${range} damage ${targetPhrase}`.trimEnd();
}

// ── Tag formatting ─────────────────────────────────────────────

/** Tags that are engine-internal and should never display to players */
const INTERNAL_TAGS = new Set(['player', 'enemy']);

/**
 * Format a tag list for display. Filters internal tags, capitalizes each.
 * Returns comma-separated string: "Attack, Spell, Ranged, Projectile, Fire".
 *
 * Dedupes case-insensitively so an ability with `tags: ['hex']` plus a derived
 * 'Hex' from its applied effect renders once, not twice.
 */
export function formatTagList(tags: string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tags) {
    if (INTERNAL_TAGS.has(t)) continue;
    const lower = t.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(cap(t));
  }
  return out.join(', ');
}

/**
 * Derive subcategory tags (Boon / Barrier / Regen / Hex / Ruin) from an ability's
 * applied status effects. Read the subcategory off applySelfEffect and
 * applyTargetEffect templates. Untagged buffs default to 'boon'; untagged debuffs
 * have no default tag (most debuffs are DoTs/controls that fit neither hex nor ruin).
 */
export function deriveBuffSubcategoryTags(
  ability: { applySelfEffect?: string; applyTargetEffect?: string },
  statusEffects?: Record<string, { category?: string; subcategory?: string }>,
): string[] {
  if (!statusEffects) return [];
  const out = new Set<string>();
  const consider = (effectId?: string) => {
    if (!effectId) return;
    const tmpl = statusEffects[effectId];
    if (!tmpl) return;
    if (tmpl.category === 'buff') {
      const sub = tmpl.subcategory ?? 'boon';
      if (sub === 'boon' || sub === 'barrier' || sub === 'regen') out.add(cap(sub));
    } else if (tmpl.category === 'debuff') {
      const sub = tmpl.subcategory;
      if (sub === 'hex' || sub === 'ruin') out.add(cap(sub));
    }
  };
  consider(ability.applySelfEffect);
  consider(ability.applyTargetEffect);
  return Array.from(out);
}

/**
 * Get the display-ready tag array (filtered, capitalized).
 * Use when you need the array, not a joined string.
 */
export function getDisplayTags(tags: string[]): string[] {
  return tags.filter(t => !INTERNAL_TAGS.has(t)).map(cap);
}

// ── Entity formatting ──────────────────────────────────────────

/** Format a race ID: "thalassan" → "Thalassan" */
export function formatRace(race: string): string {
  return cap(race);
}

/** Format a unit class/strand ID: "stormcaller" → "Stormcaller" */
export function formatUnitClass(cls: string): string {
  return cap(cls);
}

/** Format an item category: "weapon" → "Weapon" */
export function formatCategory(category: string): string {
  return cap(category);
}

/** Format an encounter tier: "elite" → "Elite" */
export function formatTier(tier: string): string {
  return cap(tier);
}

// ── Stat display names ─────────────────────────────────────────

/** Human-readable stat names for affix display. THE canonical source. */
/**
 * PoE-style affix display templates. Each stat maps to a function that
 * takes (value, modifier) and returns a full human-readable line.
 * No generic "+X% StatName" fallback — every stat has proper wording.
 */
type AffixFormatter = (v: number, mod: 'flat' | 'percent') => string;

const s = (v: number) => v >= 0 ? `+${v}` : `${v}`;  // signed number

/**
 * Player-facing damage descriptor for an ability tag. Used by tag_increased
 * affixes ("+X% increased <descriptor>"). Elements use "<Element> Damage";
 * delivery/style tags use PoE-style phrasing (Area, Damage over Time, etc.).
 * Never expose the raw tag id — "Dot Damage" or "Aoe Damage" reads like a bug.
 */
const TAG_DAMAGE_DESCRIPTORS: Record<string, string> = {
  // Physical
  steel: 'Steel Damage', brute: 'Brute Damage', finesse: 'Finesse Damage',
  // Elements
  fire: 'Fire Damage', water: 'Water Damage', lightning: 'Lightning Damage',
  nature: 'Nature Damage', wind: 'Wind Damage', earth: 'Earth Damage',
  holy: 'Holy Damage', dark: 'Dark Damage', astral: 'Astral Damage',
  // Style / delivery
  melee: 'Melee Damage',
  ranged: 'Ranged Damage',
  aoe: 'Area Damage',
  spell: 'Spell Damage',
  projectile: 'Projectile Damage',
  attack: 'Attack Damage',
  // Mechanic
  dot: 'Damage over Time',
  impact: 'Impact Damage',
  charged: 'Charged Skill Damage',
  combo: 'Combo Damage',
  trap: 'Trap Damage',
  cooldown: 'Damage with Cooldown Abilities',
};

/** Returns the player-facing damage descriptor for an ability tag. */
export function formatTagDamageLabel(tag: string): string {
  return TAG_DAMAGE_DESCRIPTORS[tag] ?? `${cap(tag)} Damage`;
}

/**
 * Compact-context label for a tag (gear-card affix summaries, etc.).
 * Drops the trailing "Damage", uses acronyms for awkward tags.
 */
const TAG_SHORT_LABELS: Record<string, string> = {
  dot: 'DoT', aoe: 'AoE', charged: 'Charged Skill',
};
export function formatTagShortLabel(tag: string): string {
  if (TAG_SHORT_LABELS[tag]) return TAG_SHORT_LABELS[tag];
  return TAG_DAMAGE_DESCRIPTORS[tag]?.replace(/\s+Damage$/, '') ?? cap(tag);
}

/** Build the full "X% increased <descriptor>" line for a tag_increased affix value. */
function tagIncreasedLine(tag: string, v: number): string {
  return `${v}% increased ${formatTagDamageLabel(tag)}`;
}

const STAT_TEMPLATES: Record<string, AffixFormatter> = {
  // ── Core stats ──
  hp:    (v, mod) => mod === 'percent' ? `${v}% increased Maximum Health` : `${s(v)} to Maximum Health`,
  spd:   (v) => `${s(v)} to Speed`,
  focus: (v) => `${s(v)} to Maximum Focus`,

  // ── Flat damage (gear) — displayed as a range: roll → min=ceil(roll*0.5), max=roll ──
  flat_steel_damage:     (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Steel Damage`,
  flat_brute_damage:     (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Brute Damage`,
  flat_finesse_damage:   (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Finesse Damage`,
  flat_fire_damage:      (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Fire Damage`,
  flat_water_damage:     (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Water Damage`,
  flat_lightning_damage: (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Lightning Damage`,
  flat_holy_damage:      (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Holy Damage`,
  flat_dark_damage:      (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Dark Damage`,
  flat_nature_damage:    (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Nature Damage`,
  flat_wind_damage:      (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Wind Damage`,
  flat_earth_damage:     (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Earth Damage`,
  flat_astral_damage:    (v) => `Adds ${Math.max(1, Math.ceil(v * 0.5))}-${v} Astral Damage`,

  // ── Damage (gear) ──
  increased_damage: (v) => `${v}% increased Damage`,

  // ── Tag-increased damage ── (one per ability tag, "X% increased <descriptor>")
  tag_increased_steel:      (v) => tagIncreasedLine('steel', v),
  tag_increased_brute:      (v) => tagIncreasedLine('brute', v),
  tag_increased_finesse:    (v) => tagIncreasedLine('finesse', v),
  tag_increased_fire:       (v) => tagIncreasedLine('fire', v),
  tag_increased_water:      (v) => tagIncreasedLine('water', v),
  tag_increased_lightning:  (v) => tagIncreasedLine('lightning', v),
  tag_increased_nature:     (v) => tagIncreasedLine('nature', v),
  tag_increased_wind:       (v) => tagIncreasedLine('wind', v),
  tag_increased_earth:      (v) => tagIncreasedLine('earth', v),
  tag_increased_holy:       (v) => tagIncreasedLine('holy', v),
  tag_increased_dark:       (v) => tagIncreasedLine('dark', v),
  tag_increased_astral:     (v) => tagIncreasedLine('astral', v),
  tag_increased_melee:      (v) => tagIncreasedLine('melee', v),
  tag_increased_ranged:     (v) => tagIncreasedLine('ranged', v),
  tag_increased_aoe:        (v) => tagIncreasedLine('aoe', v),
  tag_increased_spell:      (v) => tagIncreasedLine('spell', v),
  tag_increased_projectile: (v) => tagIncreasedLine('projectile', v),
  tag_increased_attack:     (v) => tagIncreasedLine('attack', v),
  tag_increased_impact:     (v) => tagIncreasedLine('impact', v),
  tag_increased_charged:    (v) => tagIncreasedLine('charged', v),
  tag_increased_combo:      (v) => tagIncreasedLine('combo', v),
  tag_increased_trap:       (v) => tagIncreasedLine('trap', v),

  // ── Resistance ── (PoE convention: "+X% to Resistance", flat modifier)
  physical_res:  (v) => `${s(v)}% to Physical Resistance`,
  elemental_res: (v) => `${s(v)}% to Elemental Resistance`,
  primal_res:    (v) => `${s(v)}% to Primal Resistance`,
  cosmic_res:    (v) => `${s(v)}% to Cosmic Resistance`,
  steel_res:     (v) => `${s(v)}% to Steel Resistance`,
  brute_res:     (v) => `${s(v)}% to Brute Resistance`,
  finesse_res:   (v) => `${s(v)}% to Finesse Resistance`,

  // ── Penetration ── (you don't "increase" pen, you gain a flat amount)
  physical_pen:  (v) => `${s(v)}% to Physical Penetration`,
  elemental_pen: (v) => `${s(v)}% to Elemental Penetration`,
  primal_pen:    (v) => `${s(v)}% to Primal Penetration`,
  cosmic_pen:    (v) => `${s(v)}% to Cosmic Penetration`,

  // ── Sustain ──
  life_on_hit:   (v) => `Gain ${v} Health On Hit`,
  focus_on_hit:  (v) => `Gain ${v} Focus On Hit`,
  hp_regen:          (v) => `Regenerate ${v} Health per turn`,
  focus_on_hp_regen: (v) => `Gain ${v} Focus when Health regenerates`,
  focus_regen:       (v) => `Regenerate ${v} Focus per turn`,
  focus_on_crit:     (v) => `Gain ${v} Focus On Critical Strike`,
  focus_on_kill:     (v) => `Gain ${v} Focus On Kill`,

  // ── Healing ──
  healing_done:     (v) => `${v}% increased Healing`,
  healing_received: (v) => v >= 0 ? `${v}% increased Healing Received` : `${Math.abs(v)}% reduced Healing Received`,

  // ── DoT ──
  dot_potency:    (v) => `${v}% increased Damage over Time`,
  dot_multiplier: (v) => `${v}% more Damage over Time`,
  flat_dot_damage:(v) => `${s(v)} to Damage over Time`,
  burn_dot_potency:   (v) => `${v}% increased Burn Damage over Time`,
  bleed_dot_potency:  (v) => `${v}% increased Bleed Damage over Time`,
  poison_dot_potency: (v) => `${v}% increased Poison Damage over Time`,
  decay_dot_potency:  (v) => `${v}% increased Decay Damage over Time`,
  burn_multiplier:    (v) => `${v}% more Burn Damage over Time`,
  bleed_multiplier:   (v) => `${v}% more Bleed Damage over Time`,
  poison_multiplier:  (v) => `${v}% more Poison Damage over Time`,
  decay_multiplier:   (v) => `${v}% more Decay Damage over Time`,

  // ── Status effects ──
  status_resistance:  (v) => `${v}% reduced Debuff Duration`,
  dot_damage_taken:   (v) => `Take ${v}% reduced Damage over Time`,
  chance_inflict_exposed: (v) => `${v}% chance to inflict Exposed On Hit`,

  // ── Crit / Lifesteal ──
  crit_chance:           (v) => `${s(v)}% to Critical Strike Chance`,
  increased_crit_chance: (v) => `${v}% increased Critical Strike Chance`,
  crit_multi:            (v) => `${s(v)}% to Critical Strike Multiplier`,
  lifesteal:             (v) => `Recover ${v}% of damage dealt as Health`,

  // ── Weapon archetype ──
  impact_damage:    (v) => `${v}% more Damage when consuming Fractured`,
  fervor_potency:   (v) => `${v}% increased Fervor Potency`,
  stagger_potency:  (v) => `${v}% increased Stagger Potency`,
  bonus_vs_full_hp:  (v) => `${v}% more Damage against Full Health enemies`,
  bonus_vs_wounded:  (v) => `${v}% more Damage against Wounded enemies`,
  bonus_vs_debuffed: (v) => `${v}% more Damage against Debuffed enemies`,
  break_power_increased: (v) => `${v}% increased Break Power`,
  break_power_more: (v) => `${v}% more Break Power`,
  flat_break_power: (v) => `${s(v)} Added Break Power`,
  tempo_generation: (v) => `${v}% chance to gain an additional Tempo On Hit`,
  extra_combo_hits: (v) => `${s(v)} additional Combo Hits`,
  chain_chance:     (v) => `${v}% chance for Projectiles to Chain an additional time`,
  global_momentum_reduction:  (v) => `${v}% reduced Momentum Cost`,
  global_focus_cost_reduction:(v) => `${v}% reduced Focus Cost`,

  // ── Fervor (Allies-wide resource) ──
  fervor_max_bonus:           (v) => `${s(v)} to Maximum Fervor`,
  health_on_fervor_consume:   (v) => `Recover ${v} Health per Fervor consumed`,
  damage_per_fervor_stack:    (v) => `${v}% increased Damage per active Fervor`,

  // ── Potency / duration ──
  barrier_potency: (v) => `${v}% increased Barrier Potency`,
  hex_potency:         (v) => `${v}% increased Hex Potency`,
  ruin_potency:        (v) => `${v}% increased Ruin Potency`,
  debuff_duration:     (v) => `${v}% increased Debuff Duration`,
  buff_duration:       (v) => `${v}% increased Buff Duration`,
  debuff_duration_flat:(v) => `+${v} turns to Debuff Duration`,
  buff_duration_flat:  (v) => `+${v} turns to Buff Duration`,
  boon_potency:        (v) => `${v}% increased Boon Potency`,

  // ── Barrier ──
  barrier_on_hit:        (v) => `Gain ${v} Barrier On Hit`,
  barrier_on_crit:       (v) => `Gain ${v} Barrier On Critical Strike`,
  barrier_duration:      (v) => `+${v} turns to Barrier Duration`,
  barrier_active_res:    (v) => `${s(v)}% to Elemental Resistance while Barrier is active`,
  barrier_active_dr:     (v) => `Take ${v}% reduced Damage while Barrier is active`,
  barrier_active_damage: (v) => `${v}% more Damage while Barrier is active`,
  flat_barrier_amount:   (v) => `${s(v)} Added Barrier`,
  barrier_more:          (v) => `${v}% more Barrier`,
  barrier_received:      (v) => `${v}% increased Barrier Received`,

  // ── Healing (extras — healing_done/received defined earlier in the file) ──
  flat_heal_amount:    (v) => `${s(v)} Added Healing`,
  heal_more:           (v) => `${v}% more Healing`,

  // ── Mail (heavy) mechanics ──
  guard:               (v, mod) => mod === 'percent' ? `${v}% increased Guard` : `${s(v)} to Guard`,
  heal_on_kill:        (v) => `Heal ${v}% of Maximum Health On Kill`,
  heal_on_combat_end:  (v) => `Recover ${v}% of Maximum Health at end of combat`,

  // ── Leather (medium) mechanics ──
  cornered_spd:        (v) => `${s(v)} to Speed while below 50% Health`,
  // Stored as raw momentum (10000 = 1 turn); divide by 100 to match displayed momentum units.
  momentum_on_struck:  (v) => `Gain ${Math.round(v / 100)} Momentum when struck`,
  hp_on_struck:        (v) => `Recover ${v} Health when struck`,
  focus_on_struck_armor: (v) => `Gain ${v} Focus when struck`,
  thorns_damage:       (v) => `Deal ${v} Damage to attackers when struck`,
  barrier_on_struck:   (v) => `Gain ${v} Barrier when struck`,
  chance_apply_soaked_on_struck: (v) => `${v}% chance to inflict Soaked when struck`,
  damage_taken_reduced_full_hp:  (v) => `Take ${v}% reduced Damage while at Full Health`,
  crit_avoidance:      (v) => `${v}% chance to avoid Critical Strikes`,
  first_strike_bonus:  (v) => `${v}% more Damage on first attack in combat`,
  trap_potency:        (v) => `${v}% more Trap Damage`,
  charged_potency:     (v) => `${v}% more Charged Skill Damage`,
  armor_lifesteal:     (v) => `Recover ${v}% of damage dealt as Health`,
  speed_on_kill:       (v) => `${s(v)} to Speed for 2 turns On Kill`,

  // ── Robe (light caster) mechanics ──
  focus_percent:       (v) => `${v}% increased Maximum Focus`,

  // ── Single-element resistances (shared across armor families) ──
  holy_res:      (v) => `${s(v)}% to Holy Resistance`,
  dark_res:      (v) => `${s(v)}% to Dark Resistance`,
  fire_res:      (v) => `${s(v)}% to Fire Resistance`,
  water_res:     (v) => `${s(v)}% to Water Resistance`,
  earth_res:     (v) => `${s(v)}% to Earth Resistance`,
  wind_res:      (v) => `${s(v)}% to Wind Resistance`,
  nature_res:    (v) => `${s(v)}% to Nature Resistance`,
  lightning_res: (v) => `${s(v)}% to Lightning Resistance`,
  astral_res:    (v) => `${s(v)}% to Astral Resistance`,

  // ── Minion (summoner) stats ──
  minion_damage:      (v) => `${v}% increased Minion Damage`,
  minion_health:      (v) => `${v}% increased Minion Maximum Health`,
  minion_spd:         (v) => `${s(v)} to Minion Speed`,
  minion_crit_chance: (v) => `${s(v)}% to Minion Critical Strike Chance`,
  minion_crit_multi:  (v) => `${s(v)}% to Minion Critical Strike Multiplier`,

  // ── Unique mechanics ──
  shield_on_turn_start: (v) => `Gain Barrier equal to ${v}% of Maximum Health at the start of each turn`,
  // Velara's Recantation
  lowhp_speed_67:     (v) => `${v}% increased Speed while below 67% Health`,
  lowhp_speed_33:     (v) => `${v}% increased Speed while below 33% Health`,
  hp_drain_per_turn:  (v) => `Lose ${v}% of maximum Health per turn`,
  // Quietspine
  attack_break_floor: (v) => `Your single-target Melee Attacks have a minimum ${v} Break Power`,
  // Crestwind (Lead Gust affix → Opening Gust status)
  spd_buff_at_combat_start: (v) => `${v}% increased Speed for 3 turns at start of combat`,
};

/** Format a stat value into a full PoE-style affix line */
export function formatStatLine(stat: string, modifier: 'flat' | 'percent', value: number): string {
  const template = STAT_TEMPLATES[stat];
  if (template) return template(value, modifier);
  // Mastery affixes — keyed as `mastery_<id>`. Mastery display names match
  // cap(id) for every entry in masteries.ts, so this works without configData.
  if (stat.startsWith('mastery_')) {
    const name = cap(stat.slice('mastery_'.length));
    return modifier === 'percent' ? `${s(value)}% ${name}` : `${s(value)} ${name}`;
  }
  // Fallback for unmapped stats — should not happen, add the stat above
  const pct = modifier === 'percent' ? '%' : '';
  return `${s(value)}${pct} ${stat.replace(/_/g, ' ')}`;
}

// Short-label overrides for stats whose full tooltip line doesn't derive a clean label.
const STAT_NAME_OVERRIDES: Record<string, string> = {
  impact_damage: 'Fractured Damage',
};

/** Short stat name for contexts that need a label (codex tables, stat breakdowns) */
export function formatStatName(stat: string): string {
  if (STAT_NAME_OVERRIDES[stat]) return STAT_NAME_OVERRIDES[stat];
  // Generate a short label from the template by evaluating with value=0 and stripping the number
  const template = STAT_TEMPLATES[stat];
  if (template) {
    // Use a simple name extraction — take the text after the number
    const full = template(0, 'flat');
    return full
      .replace(/^[+\-]?0%?\s*/, '')
      .replace(/^Gain |^Regenerate |^Take |^Adds |^Recover /, '')
      .replace(/^\d+\s+/, '')       // strip leading number left by verb removal (e.g. "0 Focus per turn" → "Focus per turn")
      .replace(/\s*per turn$/, '')
      .replace(/\s*On Critical Strike$/, ' On Crit')
      .replace(/\s*On Hit$/, ' On Hit')
      .replace(/\s*On Kill$/, ' On Kill')
      .trim();
  }
  if (stat.startsWith('mastery_')) {
    return cap(stat.slice('mastery_'.length));
  }
  return stat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Ability mechanic formatters ────────────────────────────

import type { AbilityConfig, StatusEffectTemplate, EnemyTemplate } from '../../types';

interface EffectLookup {
  statusEffects?: Record<string, StatusEffectTemplate>;
  /** Optional minion-template registry — used to format summon-ability tooltip lines. */
  minionTemplates?: Record<string, EnemyTemplate>;
  /** Optional ability registry — used to look up minion attack damage for summon tooltips. */
  abilities?: Record<string, AbilityConfig>;
}

/**
 * Returns the status effect template for an ability's barrier-granting buff,
 * or null if the ability does not grant a barrier. Used by skill card / tooltip /
 * codex renderers to surface the barrier amount stored in the skill rank.
 */
export function getBarrierBuffEffect(
  ability: AbilityConfig,
  config?: EffectLookup,
): StatusEffectTemplate | null {
  const effectId = ability.applySelfEffect ?? ability.applyTargetEffect;
  if (!effectId) return null;
  const effect = config?.statusEffects?.[effectId];
  return effect?.grantsBarrier ? effect : null;
}

/**
 * Optional context passed to mechanic formatting so per-rank numbers (e.g. the
 * barrier amount on a Brinewall skill) can be inlined directly into the
 * effect line. Without this, callers fall back to the generic name+duration.
 */
export interface AbilityMechanicContext {
  /** Per-rank min/max for buffs that grant a barrier. Reads from skill rank's damage range. */
  barrierRange?: { min: number; max: number };
  /** Per-rank min/max for the Poised buff's counter damage. Reads from skill rank's damage range. */
  counterDamageRange?: { min: number; max: number };
  /** Per-rank effect potency (e.g. Marked +X%, Empower +X%, Brace flat reduction). */
  potency?: number;
  /** Bonus crit multi % carried by the effect (Acuity Peak +25). */
  bonusCritMulti?: number;
  /** Per-rank DoT damage (overrides the static statusEffect.dotDamage). */
  dotDamage?: number;
  /** Per-rank regen-per-turn (overrides the static statusEffect.regenAmount). */
  regenAmount?: number;
  /** Per-rank summon HP for player-summon skills (raise_skeleton etc.). Overrides minion template HP. */
  summonHp?: number;
  /** Per-rank summon duration. Overrides "Persists until killed" with "Lasts X turns". */
  summonDurationTurns?: number;
  /** Per-rank min/max for trap abilities (Powderkeg etc). Reads from skill rank's damage range —
   *  the cast multiplier is 0, so the only place to show damage is the trap line itself. */
  trapDamageRange?: { min: number; max: number };
  /** Per-rank min/max for Manifest Blade — the flat Earth damage added per charge to the
   *  caster's next 3 damage skills. Reads from skill rank's damage range. */
  infusionRange?: { min: number; max: number };
  /** Per-rank min/max for Bristle's thorns retaliation. Reads from skill rank's damage range. */
  thornsRange?: { min: number; max: number };
  /** Per-rank charge count for charge-grant buffs (Venomcoat 2/3/4 by rank). */
  chargesGranted?: number;
  /** Per-rank min/max for the self-barrier rider on damage+barrier hybrid spells (Surge).
   *  Distinct from barrierRange (which reads from minDamage/maxDamage on pure barrier buffs);
   *  this reads from rank.barrierMin/Max so the damage portion stays separate. */
  barrierRiderRange?: { min: number; max: number };
}

/** Context handed to each effect template. PoE-style subject-first wording. */
interface EffectFormatCtx {
  /**
   * "Self: " when the effect is a side-effect on the caster of an ability that
   * targets someone else. Empty for direct-target effects (the line itself
   * makes the subject clear).
   */
  selfPrefix: string;
  /** Subject. "An ally" / "Allies" / "You" / "An enemy" / "Enemies". */
  subject: string;
  /** Possessive. "An ally's" / "Allies'" / "Your" / "An enemy's" / "Enemies'". */
  subjectPossessive: string;
  /** Object pronoun. "you" / "an ally" / "all allies" / "an enemy" / "all enemies". */
  object: string;
  /** Verb forms conjugated for the subject (singular vs. plural). */
  has: string; takes: string; deals: string; gains: string;
  recovers: string; regenerates: string; is: string; intercepts: string; survives: string;
  /**
   * Trailing target suffix — " on an ally" / " on an enemy" / "" — used by
   * Shape B keyword-form lines ("Inflicts Marked on an enemy for 3 turns")
   * that read more naturally with a trailing target phrase.
   */
  targetSuffix: string;
  /** Element of the parent ability (DoT element labels). */
  abilityElement: string;
  /** Optional per-rank values. */
  barrierRange?: { min: number; max: number };
  counterDamageRange?: { min: number; max: number };
  potency?: number;
  regenAmount?: number;
  /** Bonus crit multi % from the effect (Acuity Peak adds +25). */
  bonusCritMulti?: number;
  /** Per-rank min/max for trap abilities — used by trap formatters to render damage range. */
  trapDamageRange?: { min: number; max: number };
  /** Per-rank min/max for Manifest Blade — flat Earth added per charge to the caster's next 3 damage skills. */
  infusionRange?: { min: number; max: number };
  /** Per-rank min/max for Bristle's thorns retaliation range. */
  thornsRange?: { min: number; max: number };
  /** Per-rank DoT damage (used by formatters that mention the DoT magnitude — e.g. Venomcoat). */
  dotDamage?: number;
  /** Per-rank charge count for charge-grant buffs (Venomcoat). */
  chargesGranted?: number;
}

type EffectFormatter = (effect: StatusEffectTemplate, ctx: EffectFormatCtx) => string | string[];

const turnSuffix = (e: StatusEffectTemplate) => e.defaultTurns === 1 ? '' : 's';
const dim = (s: string) => `<span style="color:rgba(255,255,255,0.45)">${s}</span>`;

/**
 * Prescribed status-effect display registry. PoE-style subject-first wording.
 *
 * Two shapes:
 *   • Shape A — Descriptive (most buffs / stat-change debuffs). Just describe
 *     what happens, subject-first, no "Grants" / "Inflicts" prefix.
 *     ("An ally has 50% increased Speed for 2 turns")
 *   • Shape B — Tracking keyword (DoTs, amplifier debuffs, traps, named
 *     effects that gear/abilities reference by name). Lead with
 *     "Inflicts <Keyword>" or "Grants <Keyword>", then a dimmed sub-line.
 *     ("Inflicts Marked on an enemy for 3 turns" / dim: "Projectile abilities deal +25%...")
 *
 * Effects with structural numerical fields (DoT, resistance modifiers,
 * barriers) fall through to the structural branches below.
 */
const EFFECT_DISPLAY_TEMPLATES: Record<string, EffectFormatter> = {
  // ── Shape A — Buffs (subject-first description) ───────────────
  spd_up:        (e, c) => `${c.selfPrefix}${c.subject} ${c.has} <strong class="buff">50% increased Speed</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  rallied:       (e, c) => `${c.selfPrefix}${c.subject} ${c.deals} <strong class="buff">10% more Attack Damage</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  ward:          (e, c) => `${c.selfPrefix}${c.subject} ${c.has} <strong class="buff">+15 to Dark, Holy, and Astral Resistance</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  brace:         (e, c) => `${c.selfPrefix}${c.subject} ${c.takes} <strong class="buff">${c.potency ?? 5} less Damage</strong> per hit for ${e.defaultTurns} turn${turnSuffix(e)}`,
  // Fortified — stackable DR buff applied by Fortify (per-rank stack count via c.potency),
  // Fold (engine-computed from consumed Barrier — Fold's own ability line owns the rule),
  // Lockstep ally propagation (engine-emitted, no mechCtx so falls back to "1 stack"),
  // and the plate `chance_fortify_on_struck` gear proc. The ability tooltip path always
  // sets c.potency from the skill rank, so we render "Grants N stack(s) of Fortified" here.
  fortified: (e, c) => {
    const stacks = Math.max(1, Math.floor(c.potency ?? 1));
    const stackWord = stacks === 1 ? '1 stack' : `${stacks} stacks`;
    return `${c.selfPrefix}${c.subject} ${c.gains} <strong class="buff">${stackWord} of Fortified</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`;
  },
  // Bristle — self-buff. c.thornsRange carries the per-rank thorns retaliation range from
  // the skill rank's min/max. c.potency carries the bleed-potency multiplier (0.4/0.5/0.6),
  // used to compute the live bleed tick damage (bleed.dotDamage × potency).
  bristle: (e, c) => {
    const range = c.thornsRange;
    const dmg = range
      ? `<strong class="damage">${range.min} to ${range.max} Steel Damage</strong>`
      : `<strong class="damage">Steel Damage</strong>`;
    const bleedTick = c.potency != null ? Math.max(1, Math.floor(10 * c.potency)) : null;
    const lines: string[] = [
      `${c.selfPrefix}Attackers take ${dmg} and inflict <strong class="debuff">Bleed</strong> when they hit ${c.object} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    ];
    if (bleedTick != null) {
      lines.push(dim(`Bleed deals <strong class="damage">${bleedTick} Steel Damage</strong> per turn`));
    }
    return lines;
  },
  // Lockstep — self-buff. While active, every hit taken propagates 1 Fortified stack to
  // all allies. c.potency overrides stacks-per-trigger when set (currently always 1).
  lockstep: (e, c) => {
    const stacks = Math.max(1, Math.floor(c.potency ?? 1));
    const stackWord = stacks === 1 ? '1 stack' : `${stacks} stacks`;
    return `${c.selfPrefix}When ${c.subject} ${c.is} hit, all allies gain <strong class="buff">${stackWord} of Fortified</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`;
  },
  // Temper — self-buff. c.potency carries the bounce chance % (40/55/80 by rank).
  temper: (e, c) => {
    const chance = Math.floor(c.potency ?? 40);
    return `${c.selfPrefix}Debuffs landing on ${c.object} have <strong class="buff">${chance}% chance to redirect to the attacker</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`;
  },
  fire_infusion: (e, c) => `${c.selfPrefix}${c.subjectPossessive} Steel, Brute, and Finesse Damage is converted to <strong class="buff">Fire Damage</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  manifest_blade: (_e, c) => {
    const r = c.infusionRange;
    const dmg = r
      ? `<strong class="buff">+${r.min} to ${r.max} Added Earth Damage</strong>`
      : `<strong class="buff">Added Earth Damage</strong>`;
    return `${c.selfPrefix}${c.subjectPossessive} next 3 damage skills have ${dmg}`;
  },
  intercepting:  (e, c) => `${c.selfPrefix}${c.subject} ${c.intercepts} hits aimed at the protected ally for ${e.defaultTurns} turn${turnSuffix(e)}`,
  nature_regen:  (e, c) => `${c.selfPrefix}${c.subject} ${c.regenerates} <strong class="heal">${c.regenAmount ?? e.regenAmount ?? 0} Health</strong> per turn for ${e.defaultTurns} turn${turnSuffix(e)}`,
  tonic:         (e, c) => `${c.selfPrefix}${c.subject} ${c.regenerates} <strong class="heal">${c.regenAmount ?? e.regenAmount ?? 0} Health</strong> per turn for ${e.defaultTurns} turn${turnSuffix(e)}`,
  soulstitch:    (e, c) => `${c.selfPrefix}${c.subject} ${c.recovers} <strong class="heal">${c.potency ?? 20}% of damage dealt as Health</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  fury:          (e, c) => `${c.selfPrefix}${c.subject} ${c.deals} up to <strong class="buff">50% more Damage</strong> based on missing Health for ${e.defaultTurns} turn${turnSuffix(e)}`,
  defy:          (e, c) => `${c.selfPrefix}${c.subject} ${c.gains} <strong class="buff">1 Fervor</strong> when struck for ${e.defaultTurns} turn${turnSuffix(e)}`,
  taunt:         (e, c) => `${c.selfPrefix}Enemies must target ${c.object} for ${e.defaultTurns} turn${turnSuffix(e)}`,

  // ── Shape A — "Next ability" buffs (consumed on next cast) ────
  preparation:   (_e, c) => `${c.selfPrefix}${c.subjectPossessive} next ability costs <strong class="buff">25% less Momentum</strong>, then consumed`,
  catalyze:      (_e, c) => `${c.selfPrefix}${c.subjectPossessive} next ability is a <strong class="buff">guaranteed Critical Strike</strong> with <strong class="buff">+${c.bonusCritMulti ?? 25}% Critical Strike Multiplier</strong>, then consumed`,
  acuity:        (e, c) => {
    const cc = c.potency ?? 0;
    const cm = c.bonusCritMulti;
    const stat = cm
      ? `<strong class="buff">${cc}% increased Critical Strike Chance</strong> and <strong class="buff">+${cm}% to Critical Strike Multiplier</strong>`
      : `<strong class="buff">${cc}% increased Critical Strike Chance</strong>`;
    const verb = c.is === 'are' ? 'have' : 'has';
    return `${c.selfPrefix}${c.subject} ${verb} ${stat} for ${e.defaultTurns} turn${turnSuffix(e)}`;
  },
  intensify:     (e, c) => `${c.selfPrefix}Allies' Fervor-generating abilities grant <strong class="buff">+1 extra Fervor</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  // Venomcoat — per-rank chargesGranted (2/3/4) is the number of damage skills the buff lasts;
  // dotDamage is the snapshot per-stack Poison tick baked at cast time. Show both so the player
  // can evaluate the trade vs casting a damage skill directly.
  venomcoat: (_e, c) => {
    const charges = Math.max(1, Math.floor(c.chargesGranted ?? 2));
    const chargeWord = charges === 1 ? 'next ability' : `next ${charges} abilities`;
    const verb = charges === 1 ? 'inflicts' : 'inflict';
    const head = `${c.selfPrefix}${c.subjectPossessive} ${chargeWord} ${verb} <strong class="debuff">Poison</strong>, then consumed`;
    const dot = c.dotDamage;
    if (dot && dot > 0) {
      return [
        head,
        `<strong class="debuff">Poison</strong> deals <strong class="element-nature">${dot} Nature</strong> damage per turn`,
      ];
    }
    return head;
  },
  raised_shield: (_e, c) => `${c.selfPrefix}Next hit against ${c.object} deals <strong class="buff">40% reduced Damage</strong>, then consumed`,
  recomposed:    (_e, c) => `${c.selfPrefix}${c.subjectPossessive} next ability costs <strong class="buff">0 Focus</strong>, then consumed`,
  vanish:        (e, c) => [
    `${c.selfPrefix}${c.subject} cannot be targeted for ${e.defaultTurns} turn${turnSuffix(e)}`,
    `${c.subjectPossessive} next ability deals <strong class="buff">${c.potency ?? 15}% more Damage</strong>, then consumed`,
  ],
  poised:        (e, c) => {
    const range = c.counterDamageRange;
    const dmg = range
      ? `<strong class="damage">${range.min} to ${range.max} Finesse Damage</strong>`
      : `<strong class="damage">Finesse Damage</strong>`;
    return [
      `${c.selfPrefix}${c.subject} ${c.gains} <strong class="buff">2 Riposte counters</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
      `Each counter deals ${dmg} to the attacker and applies <strong class="buff">1 Tempo</strong>`,
    ];
  },

  // ── Shape A — Stat-change debuffs (subject-first) ─────────────
  spd_down:         (e, c) => `${c.subject} ${c.has} <strong class="debuff">33% reduced Speed</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  weakened:         (e, c) => `${c.subject} ${c.deals} <strong class="debuff">20% less Damage</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  smothered:        (e, c) => `${c.subject} ${c.has} <strong class="debuff">${c.potency ?? 30}% reduced Healing Received and Lifesteal</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  paralyzed:        (e, c) => `${c.subject} ${c.has} <strong class="debuff">33% reduced Speed</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  blinded:          (e, c) => `${c.subject} ${c.has} <strong class="debuff">30% chance to miss</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  silenced:         (e, c) => `${c.subjectPossessive} <strong class="debuff">Focus Costs are doubled</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  confusion:        (_e, c) => `${c.subjectPossessive} next ability hits a <strong class="debuff">random target</strong>`,

  // ── Shape B — Tracking debuffs (Inflicts <Keyword> + dimmed) ──
  hunters_mark: (e, c) => [
    `Inflicts <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    dim(`Projectile abilities deal <strong class="damage">${c.potency ?? 25}% increased Damage</strong> to this target`),
  ],
  fractured: (e, c) => [
    `Inflicts <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    dim(`Next Impact ability deals <strong class="damage">50% increased Damage</strong>, then consumes Fractured`),
  ],
  exposed: (e, c) => [
    `Inflicts <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    dim(`Next Exploit ability deals <strong class="damage">30% increased Damage</strong>, then consumes Exposed`),
  ],
  rooted: (e, c) => [
    `Inflicts <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    dim(`Cannot gain Speed buffs; existing Haste is removed`),
  ],
  condemn: (e, c) => [
    `Inflicts <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    dim(`All attacks against this target deal <strong class="damage">${c.potency ?? 10}% increased Damage</strong>`),
  ],

  // ── Momentum disruption (Shape A — subject-first descriptive) ─
  // These keywords are not gear-referenced and aren't consumed by other abilities, so they
  // don't need the Shape B "Inflicts X" framing. Single smooth descriptive line per debuff.
  // Momentum push uses displayMomentum (raw / 100) to match Momentum Cost scale.
  stagger: (e, c) => {
    const push = displayMomentum(e.momentumPenalty ?? 0);
    return `${c.selfPrefix}${c.subject} ${c.is} <strong class="debuff">Staggered</strong>, pushed back <strong class="debuff">${push}</strong> Momentum`;
  },
  stilled: (e, c) => {
    const push = displayMomentum(e.momentumPenalty ?? 0);
    return `${c.selfPrefix}${c.subject} ${c.is} <strong class="debuff">Stilled</strong>, pushed back <strong class="debuff">${push}</strong> Momentum`;
  },
  spore_cloud: (e, c) => {
    const push = displayMomentum(e.momentumPenalty ?? 0);
    return `${c.selfPrefix}${c.subject} ${c.is} caught in a <strong class="debuff">Spore Cloud</strong>, pushed back <strong class="debuff">${push}</strong> Momentum`;
  },
  // Stunned has no static momentumPenalty — the push is computed per cast from the source
  // ability's tempoConsumeStun. The number lives on that ability tooltip, so we just describe
  // the keyword state here.
  stunned: (_e, c) => `${c.selfPrefix}${c.subject} ${c.is} <strong class="debuff">Stunned</strong>`,

  // ── Shape B — Traps (Plants <Name>, trigger line, riders) ─────
  // Trap tooltips read as plant → trigger → effect. Damage range stays at the
  // tooltip's top-level "Trap deals X to Y" line (gear-modified estimate). Here
  // we render the plant line, the trigger condition, and any per-trigger riders
  // as PRIMARY (non-dim) prose — for a trap, the trigger IS the mechanic, not
  // a footnote. "Plants" + "Triggers" is unified across the family.
  trap_powderkeg: (e, c) => {
    const bonus = e.trapPerStackBonus ?? 5;
    return [
      `Plants <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
      `Triggers at the start of the target's turn if they have <strong>Tempo</strong>`,
      `<strong class="damage">+${bonus}%</strong> increased Damage per Tempo consumed`,
    ];
  },
  trap_vise: (e, c) => [
    `Plants <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    `Triggers at the start of the target's turn if they are <strong class="debuff">Broken</strong>`,
  ],
  // Sigil of Echoes — Weave-craft sigil that mirrors the next ally hit as Astral damage.
  // Single bolt, snapshot-attacker stats, consumed on trigger.
  sigil_of_echoes: (e, c) => [
    `Plants <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    `Triggers when an ally Damage ability hits this enemy`,
  ],
  // Amplifier-shape trap — no trigger event, passive while planted.
  trap_lightning_rod: (e, c) => [
    `Plants <strong class="debuff">${e.name}</strong>${c.targetSuffix} for ${e.defaultTurns} turn${turnSuffix(e)}`,
    `While planted, single-target abilities that hit this enemy Chain to <strong>1</strong> additional enemy for <strong class="damage">${c.potency ?? 50}%</strong> Damage`,
  ],

  // ── Enemy-only (subject-first description) ────────────────────
  str_up: (e, c) => `${c.subject} ${c.deals} <strong class="buff">50% increased Damage</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  arm_up: (e, c) => `${c.subject} ${c.has} <strong class="buff">+20 to all Resistances</strong> for ${e.defaultTurns} turn${turnSuffix(e)}`,
  regen:  (_e, c) => `${c.subject} ${c.recovers} <strong class="heal">${c.regenAmount ?? 15} Health</strong> per turn`,
  // shadow_burn / burn / bleed / poison — handled by the DoT branch.
  // martial_res_up / sunder / brittle / soaked / despair — handled by resistance-modifier branch.
  // brinewall / tideguard / iron_wall — handled by the grantsBarrier branch.
};

/**
 * Build the subject-first context for an effect display. Resolves the subject
 * phrase ("An ally" / "Allies" / "You" / "An enemy" / "Enemies"), its
 * possessive and object forms, and conjugated verb forms. Adds "Self: " prefix
 * when the effect is a side-effect on the caster of an ability that targets
 * someone else.
 */
function buildEffectContext(
  effect: StatusEffectTemplate,
  abilityTarget: string | undefined,
  context: 'target' | 'self',
  abilityElement: string,
  mechCtx?: AbilityMechanicContext,
): EffectFormatCtx {
  const selfPrefix = context === 'self' ? 'Self: ' : '';
  // Enemy-source effects on a self-target render in third person ("The enemy")
  // because the player views these in bestiary/telegraph tooltips, not as a
  // self-buff on themselves.
  const isEnemySelf = effect.source === 'enemy' && (abilityTarget === 'self' || context === 'self');

  let subject: string;
  let subjectPossessive: string;
  let object: string;
  let targetSuffix: string;

  if (isEnemySelf) {
    subject = 'The enemy';
    subjectPossessive = "The enemy's";
    object = 'the enemy';
    targetSuffix = '';
  } else if (context === 'self') {
    subject = 'You';
    subjectPossessive = 'Your';
    object = 'you';
    targetSuffix = '';
  } else {
    switch (abilityTarget) {
      case 'self':
        subject = 'You'; subjectPossessive = 'Your'; object = 'you'; targetSuffix = '';
        break;
      case 'all_allies':
        subject = 'Allies'; subjectPossessive = "Allies'"; object = 'all allies'; targetSuffix = ' on all allies';
        break;
      case 'single_ally':
        subject = 'An ally'; subjectPossessive = "An ally's"; object = 'an ally'; targetSuffix = ' on an ally';
        break;
      case 'all_enemies':
        subject = 'Enemies'; subjectPossessive = "Enemies'"; object = 'all enemies'; targetSuffix = ' on all enemies';
        break;
      case 'single_enemy':
        subject = 'An enemy'; subjectPossessive = "An enemy's"; object = 'an enemy'; targetSuffix = ' on an enemy';
        break;
      default:
        // Fallback for unmapped ability targets — generic third-person.
        subject = 'The target';
        subjectPossessive = "The target's";
        object = 'the target';
        targetSuffix = '';
    }
  }

  const plural = subject === 'Allies' || subject === 'Enemies' || subject === 'You';

  return {
    selfPrefix,
    subject,
    subjectPossessive,
    object,
    has:         plural ? 'have'       : 'has',
    takes:       plural ? 'take'       : 'takes',
    deals:       plural ? 'deal'       : 'deals',
    gains:       plural ? 'gain'       : 'gains',
    recovers:    plural ? 'recover'    : 'recovers',
    regenerates: plural ? 'regenerate' : 'regenerates',
    is:          plural ? 'are'        : 'is',
    intercepts:  plural ? 'intercept'  : 'intercepts',
    survives:    plural ? 'survive'    : 'survives',
    targetSuffix,
    abilityElement,
    barrierRange: mechCtx?.barrierRange,
    counterDamageRange: mechCtx?.counterDamageRange,
    potency: mechCtx?.potency,
    regenAmount: mechCtx?.regenAmount,
    bonusCritMulti: mechCtx?.bonusCritMulti,
    trapDamageRange: mechCtx?.trapDamageRange,
    infusionRange: mechCtx?.infusionRange,
    thornsRange: mechCtx?.thornsRange,
    dotDamage: mechCtx?.dotDamage,
    chargesGranted: mechCtx?.chargesGranted,
  };
}

/**
 * Format a status effect into display lines.
 *
 * Lookup order:
 *   1. EFFECT_DISPLAY_TEMPLATES — prescribed formatter (preferred)
 *   2. Structural branches — barrier / DoT / resistance modifier
 *   3. Default fallback — name + duration + description sub-line
 */
function formatEffectLines(
  effect: StatusEffectTemplate,
  abilityElement: string,
  context: 'target' | 'self',
  mechCtx?: AbilityMechanicContext,
  abilityTarget?: string,
): string[] {
  const lines: string[] = [];
  const cls = effect.category === 'buff' ? 'buff' : 'debuff';
  const ctx = buildEffectContext(effect, abilityTarget, context, abilityElement, mechCtx);
  const dur = effect.defaultTurns > 0 && effect.defaultTurns < 99
    ? ` for ${effect.defaultTurns} turn${turnSuffix(effect)}`
    : '';

  // 1. Prescribed registry — preferred path. One source of truth, like affixes.
  const tmpl = EFFECT_DISPLAY_TEMPLATES[effect.id];
  if (tmpl) {
    const result = tmpl(effect, ctx);
    if (Array.isArray(result)) lines.push(...result);
    else lines.push(result);
    return lines;
  }

  // 2. Structural branches for effects that don't need bespoke wording.

  // Barriers — keep "Grants" form. Numeric grants are PoE-faithful for
  // amount-based effects ("Grants a 60 to 100 Barrier on an ally for 4 turns").
  if (effect.grantsBarrier && mechCtx?.barrierRange) {
    const { min, max } = mechCtx.barrierRange;
    lines.push(`${ctx.selfPrefix}Grants a <strong class="${cls}">${min} to ${max} Barrier</strong>${ctx.targetSuffix}${dur}`);
    if (effect.barrierEffects) lines.push(...effect.barrierEffects);
    return lines;
  }

  // DoTs — PoE-style: two clean declarative statements. First applies the status,
  // second describes what the status does. Lets the player scan for the keyword AND
  // see the damage magnitude prominently, with full element coloring on the tick.
  const resolvedDot = mechCtx?.dotDamage ?? effect.dotDamage;
  if (resolvedDot) {
    const dotElId = effect.dotElement ?? abilityElement;
    const dotEl = cap(dotElId);
    const dotElCls = elementClass(dotElId);
    lines.push(`Inflicts <strong class="${cls}">${effect.name}</strong>${ctx.targetSuffix}${dur}`);
    lines.push(`<strong class="${cls}">${effect.name}</strong> deals <strong class="${dotElCls}">${resolvedDot} ${dotEl}</strong> damage per turn`);
    return lines;
  }

  // Resistance modifiers — Shape B: keyword + dimmed res values.
  if (effect.resistanceModifiers) {
    const entries = Object.entries(effect.resistanceModifiers);
    const verb = effect.category === 'buff' ? 'Grants' : 'Inflicts';
    lines.push(`${ctx.selfPrefix}${verb} <strong class="${cls}">${effect.name}</strong>${ctx.targetSuffix}${dur}`);
    const byVal = new Map<number, string[]>();
    for (const [el, val] of entries) {
      if (!byVal.has(val)) byVal.set(val, []);
      byVal.get(val)!.push(cap(el));
    }
    for (const [val, elements] of byVal) {
      const elLabel = elements.length >= 10
        ? 'all'
        : elements.length <= 2
          ? elements.join(' and ')
          : elements.slice(0, -1).join(', ') + ', and ' + elements[elements.length - 1];
      const sign = val >= 0 ? '+' : '';
      lines.push(dim(`${sign}${val} to ${elLabel} Resistance`));
    }
    return lines;
  }

  // 3. Default fallback. Adding a registry entry above is the fix for any
  // effect that hits this branch.
  const verb = effect.category === 'buff' ? 'Grants' : 'Inflicts';
  lines.push(`${ctx.selfPrefix}${verb} <strong class="${cls}">${effect.name}</strong>${ctx.targetSuffix}${dur}`);
  if (effect.description) lines.push(dim(effect.description));
  return lines;
}

/**
 * Build an array of human-readable mechanic lines for an ability.
 * Each line is an HTML string. Covers ALL mechanical fields.
 * Use in tooltips, codex, shop — one function, consistent everywhere.
 */
export function formatAbilityMechanics(
  ability: AbilityConfig,
  config?: EffectLookup,
  mechCtx?: AbilityMechanicContext,
): string[] {
  const lines: string[] = [];

  // Multi-hit
  if (ability.hitCount && ability.hitCount > 1) {
    lines.push(`Hits <strong>${ability.hitCount}</strong> times`);
  }

  // Chain bounce — branches by shape:
  //  - chainAlternates (Crosscurrent): bounce is an ally heal, not an enemy chain.
  //  - default (Crestfall, Ricochet Bolt, Chain Lightning): bounces hit additional enemies.
  //  - chainHealPercent on a non-alternating chain (Crestfall): each hop also pulses an ally heal.
  //  - chainBonusPerSoaked (Chain Lightning only): extra hops per Soaked enemy on the field.
  if (ability.chainBounce) {
    const hops = typeof ability.chainBounce === 'number' ? ability.chainBounce : 1;
    if (ability.chainAlternates) {
      const healPct = ability.chainHealPercent ?? 0;
      lines.push(`Bounces to <strong>1</strong> ally, healing for <strong class="heal">${healPct}%</strong> of damage dealt`);
    } else {
      const enemyWord = hops === 1 ? 'enemy' : 'enemies';
      lines.push(`Chains to <strong>${hops}</strong> additional ${enemyWord}`);
      if (ability.chainHealPercent && ability.chainHealPercent > 0) {
        lines.push(`Chain hops heal an ally for <strong class="heal">${ability.chainHealPercent}%</strong> of damage dealt`);
      }
    }
  }
  if (ability.chainBonusPerSoaked) {
    lines.push(`Chains <strong>${ability.chainBonusPerSoaked}</strong> additional time per <strong class="debuff">Soaked</strong> enemy on the field at cast`);
  }
  if (ability.healChainBounce) {
    lines.push(`Heal bounces to <strong>1</strong> additional ally`);
  }

  // Target effect
  if (ability.applyTargetEffect) {
    const effect = config?.statusEffects?.[ability.applyTargetEffect];
    if (effect) {
      lines.push(...formatEffectLines(effect, ability.element, 'target', mechCtx, ability.target));
    }
  }

  // Temper (Fortification): consumes ALL Barrier on self, grants Fortified stacks at a
  // per-rank ratio (effectPotency = Barrier per stack, lower = better). Stores the ratio,
  // NOT a stack count, so we can't route through the standard applySelfEffect formatter
  // (which would render "Grants 30 stacks of Fortified"). Custom lines instead.
  const isTemperAbility = ability.id === 'temper';
  if (isTemperAbility) {
    const ratio = mechCtx?.potency ?? 30;
    lines.push(`Consumes all your <strong class="buff">Barrier</strong>`);
    lines.push(`Gain <strong class="buff">1 stack of Fortified</strong> per <strong class="buff">${ratio} Barrier</strong> consumed (max <strong class="buff">5 stacks</strong>)`);
  }

  // Self effect — drop the "Self:" prefix when the whole ability is already
  // self-targeted (otherwise it's a redundant label on pure self-buffs).
  // Skip when an ability-id-specific branch above already authored the self-effect lines.
  if (ability.applySelfEffect && !isTemperAbility) {
    const effect = config?.statusEffects?.[ability.applySelfEffect];
    if (effect) {
      const isPureSelf = ability.target === 'self';
      lines.push(...formatEffectLines(effect, ability.element, isPureSelf ? 'target' : 'self', mechCtx, 'self'));
    }
  }

  // Damage+barrier hybrid: render the barrier-rider line inline. The rank carries a
  // separate barrierMin/Max range (not the damage range). Shape B Barrier wording per
  // wordsmith: "Grants a X to Y Barrier on yourself for N turns" — no "Self:" prefix.
  if (ability.appliesSelfBarrierEffect) {
    const effect = config?.statusEffects?.[ability.appliesSelfBarrierEffect];
    const range = mechCtx?.barrierRiderRange;
    if (effect?.grantsBarrier && range) {
      const turns = effect.defaultTurns;
      const dur = turns > 0 && turns < 99 ? ` for ${turns} turn${turns === 1 ? '' : 's'}` : '';
      lines.push(`Grants a <strong class="buff">${range.min} to ${range.max} Barrier</strong> on yourself${dur}`);
      if (effect.barrierEffects) lines.push(...effect.barrierEffects);
    }
  }

  // Spread effect
  if (ability.spreadEffect) {
    const effect = config?.statusEffects?.[ability.spreadEffect];
    const name = effect?.name ?? cap(ability.spreadEffect);
    lines.push(`Spreads <strong class="debuff">${name}</strong> to all enemies`);
  }

  // Spread sickness DoTs (Plague): pools poison and decay across hit enemies and copies them to each
  if ((ability as any).spreadAllDots) {
    lines.push(`Spreads <strong class="debuff">Poison</strong> and <strong class="debuff">Decay</strong> to all enemies`);
  }

  // Lifesteal
  if (ability.baseLifesteal) {
    lines.push(`Recover <strong class="heal">${ability.baseLifesteal}%</strong> of damage dealt as Health`);
  }

  // Conditional lifesteal
  if (ability.lifestealVsEffect && ability.lifestealVsPercent) {
    const effect = config?.statusEffects?.[ability.lifestealVsEffect];
    const name = effect?.name ?? cap(ability.lifestealVsEffect);
    lines.push(`Recover <strong class="heal">${ability.lifestealVsPercent}%</strong> of damage dealt as Health vs <strong class="debuff">${name}</strong> targets`);
  }

  // Stack-scaling flat lifesteal (Pyre Drink — flat HP per stack of named effect on target)
  const stackLeech = (ability as any).lifestealPerTargetStack as { effectType: string; healPerStack: number } | undefined;
  if (stackLeech) {
    const effect = config?.statusEffects?.[stackLeech.effectType];
    const name = effect?.name ?? cap(stackLeech.effectType);
    lines.push(`Recover <strong class="heal">${stackLeech.healPerStack} Health</strong> per <strong class="debuff">${name}</strong> stack on target`);
  }

  // Bonus vs debuffed
  if (ability.bonusVsDebuffed) {
    lines.push(`<strong class="damage">${ability.bonusVsDebuffed}%</strong> increased Damage against Debuffed enemies`);
  }

  // Debuff-count rider (Harrow): scales damage with active debuff count on the target
  if ((ability as any).debuffCountBonus) {
    lines.push(`<strong class="damage">+${(ability as any).debuffCountBonus}%</strong> increased Damage per active debuff on the target`);
  }

  // Party-debuff scaling (Wickwidow's Ember Slam): damage scales with stacks of a specific
  // debuff active across the player party. Tooltip per /wordsmith.
  const partyMult = (ability as any).damageMultPerPartyEffect as { effectType: string; perStack: number } | undefined;
  if (partyMult) {
    const effect = config?.statusEffects?.[partyMult.effectType];
    const name = effect?.name ?? cap(partyMult.effectType);
    const pct = Math.round(partyMult.perStack * 100);
    lines.push(`Deals <strong class="damage">${pct}% increased Damage</strong> per <strong class="debuff">${name}</strong> on the party`);
  }

  // Tempo generation
  if (ability.tempoGenerate) {
    lines.push(`Generates <strong class="buff">${ability.tempoGenerate}</strong> Tempo On Hit`);
  }
  if (ability.tempoGenerateFromStealth) {
    lines.push(`Generates <strong class="buff">${ability.tempoGenerateFromStealth}</strong> Tempo On Hit from Stealth`);
  }
  if (ability.tempoOnCrit) {
    lines.push(`Generates <strong class="buff">${ability.tempoOnCrit}</strong> Tempo On Critical Strike`);
  }

  // Tempo consumption — each on its own line
  const anyTempoConsume = !!(ability.tempoConsumeBonus || ability.tempoConsumeStun || ability.tempoConsumeCrit || ability.tempoConsumeCritMulti || ability.tempoConsumePen || ability.tempoConsumeLeech || ability.tempoConsumeHits || ability.tempoConsumeChain);
  if (anyTempoConsume) {
    lines.push(`Consumes all Tempo on target`);
  }
  if (ability.tempoConsumeBonus) {
    lines.push(`<strong class="damage">+${ability.tempoConsumeBonus}%</strong> Increased Damage per Tempo consumed`);
  }
  if (ability.tempoConsumeStun) {
    lines.push(`Pushes target back <strong class="debuff">${displayMomentum(ability.tempoConsumeStun)}</strong> Momentum per Tempo consumed`);
  }
  if (ability.tempoConsumeCrit) {
    lines.push(`<strong>+${ability.tempoConsumeCrit}%</strong> Crit Chance per Tempo consumed`);
  }
  if (ability.tempoConsumeCritMulti) {
    lines.push(`<strong>+${ability.tempoConsumeCritMulti}%</strong> Crit Multiplier per Tempo consumed`);
  }
  if (ability.tempoConsumePen) {
    const el = ability.element ? ability.element.charAt(0).toUpperCase() + ability.element.slice(1) + ' ' : '';
    lines.push(`<strong class="damage">-${ability.tempoConsumePen}</strong> ${el}Resistance per Tempo consumed`);
  }
  if (ability.tempoConsumeLeech) {
    lines.push(`<strong class="buff">+${ability.tempoConsumeLeech}%</strong> Lifesteal per Tempo consumed`);
  }
  if (ability.tempoConsumeHits) {
    lines.push(`Hits <strong>${ability.tempoConsumeHits === 1 ? '1' : `${ability.tempoConsumeHits}`}</strong> additional time per Tempo consumed`);
  }
  if (ability.tempoConsumeMomentumRefund) {
    lines.push(`Recovers <strong class="buff">${displayMomentum(ability.tempoConsumeMomentumRefund)}</strong> Momentum On Hit`);
  }
  if (ability.tempoConsumeChain) {
    lines.push(`Chain bounces <strong>${ability.tempoConsumeChain === 1 ? '1' : `${ability.tempoConsumeChain}`}</strong> additional time per Tempo consumed`);
  }
  // chainAlternates wording lives in the chainBounce branch above (Crosscurrent shape).

  // Ability penetration: unconditional flat pen on the primary element
  if ((ability as any).abilityPen) {
    const el = ability.element ? ability.element.charAt(0).toUpperCase() + ability.element.slice(1) + ' ' : '';
    lines.push(`Pierces <strong class="damage">${(ability as any).abilityPen}</strong> ${el}Resistance`);
  }

  // Bonus vs low HP
  if ((ability as any).bonusVsLowHp) {
    lines.push(`<strong class="damage">35%</strong> increased Damage against targets below <strong>${(ability as any).bonusVsLowHp}%</strong> Health`);
  }

  // Break — flat additive (rare, per-ability) and Broken-state riders. The base range is owned
  // by the structured stat block ("Builds: X to Y Break Power") so we don't duplicate it here.
  if (ability.flatBreakPower) {
    lines.push(`<strong class="damage">+${ability.flatBreakPower}</strong> Added Break Power`);
  }
  if (ability.bonusVsBroken) {
    lines.push(`<strong class="damage">${ability.bonusVsBroken}%</strong> increased Damage against <strong class="debuff">Broken</strong> enemies`);
  }
  if (ability.partyHealPercentMaxHpVsBroken) {
    lines.push(`Hits against <strong class="debuff">Broken</strong> enemies heal Allies for <strong class="heal">${ability.partyHealPercentMaxHpVsBroken}%</strong> of Maximum Health`);
  }
  if (ability.applyTargetEffectOnBroken) {
    const tmpl = config?.statusEffects?.[ability.applyTargetEffectOnBroken];
    const name = tmpl?.name ?? ability.applyTargetEffectOnBroken;
    lines.push(`Applies <strong class="debuff">${name}</strong> against <strong class="debuff">Broken</strong> enemies`);
  }

  // Amplifier-debuff consumers — surfaced from the ability's tag (exploit/impact)
  // rather than per-ability fields. Every exploit-tagged ability eats Exposed for
  // +30%, every impact-tagged ability eats Fractured for +50% (scaled further by
  // the attacker's Impact Damage stat at runtime). Showing the static base on the
  // tooltip mirrors how PoE surfaces "consumes" lines on the gem.
  const abilityTags = ability.tags ?? [];
  if (abilityTags.includes('impact')) {
    lines.push(`Consumes <strong class="debuff">Fractured</strong> for <strong class="damage">+50%</strong> Damage`);
  }
  if (abilityTags.includes('exploit')) {
    lines.push(`Consumes <strong class="debuff">Exposed</strong> for <strong class="damage">+30%</strong> Damage`);
  }

  // Fervor consumption
  const fervorBonus = (ability as any).fervorConsumeBonus;
  const partyHealPerFervor = (ability as any).partyHealPercentMaxHpPerFervorStack;
  if (fervorBonus || partyHealPerFervor) {
    lines.push(`Consumes all Fervor`);
    if (fervorBonus) {
      lines.push(`<strong class="damage">+${fervorBonus}%</strong> Increased Damage per Fervor consumed`);
    }
    // Fervor potency rider — applied debuff's potency scales (Blight).
    if (fervorBonus && (ability as any).fervorScalesEffectPotency && ability.applyTargetEffect) {
      const effect = (config?.statusEffects as any)?.[ability.applyTargetEffect];
      const effectName = effect?.name ?? cap(ability.applyTargetEffect);
      const effectClass = effect?.category === 'buff' ? 'buff' : 'debuff';
      lines.push(`Inflicted <strong class="${effectClass}">${effectName}</strong> has <strong class="damage">${fervorBonus}%</strong> increased Effect per Fervor consumed`);
    }
    // Fervor duration rider — applied debuff's duration extends (Immolate).
    const turnsPer = (ability as any).fervorExtendsEffectTurnsPer;
    if (turnsPer && ability.applyTargetEffect) {
      const effect = (config?.statusEffects as any)?.[ability.applyTargetEffect];
      const effectName = effect?.name ?? cap(ability.applyTargetEffect);
      const effectClass = effect?.category === 'buff' ? 'buff' : 'debuff';
      lines.push(`Inflicted <strong class="${effectClass}">${effectName}</strong> lasts <strong class="buff">1</strong> additional turn per <strong>${turnsPer}</strong> Fervor consumed`);
    }
    // Fervor lifesteal rider — lifesteal scales (Devour).
    const leechPerStack = (ability as any).fervorLifestealPerStack;
    if (leechPerStack) {
      lines.push(`<strong class="heal">+${leechPerStack}%</strong> Lifesteal per Fervor consumed`);
    }
    // Fervor party-heal rider — each ally heals for N% Max HP per Fervor consumed (Reckoning).
    // Per-rank effectPotency (mechCtx.potency) overrides the ability default.
    if (partyHealPerFervor) {
      const pct = mechCtx?.potency ?? partyHealPerFervor;
      lines.push(`Heals Allies for <strong class="heal">${pct}% of Maximum Health</strong> per Fervor consumed`);
    }
  }

  // Fervor generation
  if ((ability as any).fervorOnCast) {
    lines.push(`Generates <strong class="buff">${(ability as any).fervorOnCast}</strong> Fervor On Cast`);
  }
  if ((ability as any).fervorOnKill) {
    lines.push(`Generates <strong class="buff">${(ability as any).fervorOnKill}</strong> Fervor On Kill`);
  }
  if ((ability as any).fervorOnSuperEffective) {
    lines.push(`Generates <strong class="buff">${(ability as any).fervorOnSuperEffective}</strong> Fervor On Super-Effective Hit`);
  }
  if ((ability as any).fervorOnStealth) {
    lines.push(`Generates <strong class="buff">${(ability as any).fervorOnStealth}</strong> Fervor from Stealth`);
  }

  // Cleanse
  if (ability.type === 'cleanse') {
    const subs = (ability as any).cleanseSubcategory as ('hex' | 'ruin')[] | undefined;
    const ct = (ability as any).cleanseTarget as string | string[] | undefined;
    const targets = Array.isArray(ct) ? ct : ct ? [ct] : null;
    if (subs && subs.length > 0) {
      // Subcategory cleanse (e.g. Cleanse — Hex or Ruin). Display order is alphabetical for
      // readability; the engine priority order lives in the config array.
      const labels = [...subs].sort().map(s => `<strong class="debuff">${cap(s)}</strong>`);
      const joined = labels.length === 1
        ? labels[0]
        : labels.slice(0, -1).join(', ') + ' or ' + labels.at(-1);
      lines.push(`<strong class="buff">Removes</strong> a ${joined} from an ally`);
    } else if (targets && targets.length > 0) {
      const labels = targets.map(t => {
        const eff = config?.statusEffects?.[t];
        return `<strong class="debuff">${eff?.name ?? cap(t)}</strong>`;
      });
      const joined = labels.length === 1
        ? labels[0]
        : labels.slice(0, -1).join(', ') + ' or ' + labels.at(-1);
      lines.push(`<strong class="buff">Removes</strong> ${joined} from an ally`);
    } else {
      lines.push(`<strong class="buff">Removes one debuff</strong> from an ally`);
    }
  }

  // % Max HP heal (Grit-style self-heals — Shrug Off and any future siblings).
  // Per-rank effectPotency (mechCtx.potency) overrides the ability default.
  if (ability.healPercentMaxHp !== undefined) {
    const pct = mechCtx?.potency ?? ability.healPercentMaxHp;
    lines.push(`Recovers <strong class="heal">${pct}% of Maximum Health</strong>`);
  }

  // Focus restore — pure focus-restore self-buffs (Clarity). Detected via an
  // explicit ability.focusRestore, or a self-buff with no status effect attached
  // where the rank carries effectPotency (the focus amount).
  const isPureFocusRestore = ability.type === 'buff'
    && !ability.applyTargetEffect
    && !ability.applySelfEffect;
  const focusAmount = ability.focusRestore ?? (isPureFocusRestore ? mechCtx?.potency : undefined);
  if (focusAmount && focusAmount > 0) {
    lines.push(`Recovers <strong class="buff">${focusAmount}</strong> Focus`);
  }

  // Timeline push — advances target ally's momentum on cast
  if ((ability as any).advanceAllyMomentum) {
    const display = Math.round((ability as any).advanceAllyMomentum / 100);
    lines.push(`Advances an ally by <strong class="buff">${display}</strong> Momentum`);
  }

  // Summon — describe the minion: HP, attacks, persistence, group cap.
  // Pulls minion template + minion abilities from configData. Falls back gracefully
  // if config lookups aren't available (e.g. early-load codex render).
  if (ability.type === 'summon' && ability.summonTemplateId) {
    lines.push(...formatSummonLines(ability, config, mechCtx));
  }

  return lines;
}

// ── Status pill tooltip formatting ────────────────────────────

/**
 * Per-effect pill-tooltip formatters. The status pill tooltip shows what an
 * effect IS DOING right now (no "Inflicts/Grants" prefix, no target, no
 * duration suffix — those belong on ability prediction tooltips). The header
 * already shows the effect name + buff/debuff badge, so the body skips the
 * name. One terse mechanical fact per line.
 *
 * Effects without an entry here fall through to structural rendering (DoT
 * damage, resistance modifiers, regen, barrier) and finally to the template's
 * static `description` field.
 */
type PillRuntime = { multiplier?: number; dotDamage?: number; regenAmount?: number; stackCount?: number; potency?: number; bonusCritMulti?: number };
type PillFormatter = (effect: StatusEffectTemplate, runtime: PillRuntime) => string | string[];

/** Convert a multiplier to the % "increased / reduced" amount: 0.67 → 33, 1.5 → 50. */
function pctFromMult(mult: number | undefined, fallback: number): number {
  if (mult === undefined) return fallback;
  return Math.abs(Math.round((mult - 1) * 100));
}

const PILL_DISPLAY_TEMPLATES: Record<string, PillFormatter> = {
  // ── Buffs ──
  spd_up:        (e, r) => `<strong class="buff">${pctFromMult(r.multiplier ?? e.multiplier, 50)}% increased Speed</strong>`,
  preparation:   (e, r) => `<strong class="buff">${pctFromMult(r.multiplier ?? e.multiplier, 25)}% reduced Momentum Cost</strong>`,
  rallied:       () => `<strong class="buff">10% more Attack Damage</strong>`,
  ward:          () => `<strong class="buff">+15</strong> to Dark, Holy, and Astral Resistance`,
  brace:         () => `Reduces damage taken by a flat amount per hit`,
  taunt:         () => `Enemies must target this unit`,
  fire_infusion: () => `Steel, Brute, and Finesse Damage converted to <strong class="buff">Fire Damage</strong>`,
  manifest_blade: () => `Next 3 damage skills have <strong class="buff">Added Earth Damage</strong>`,
  intercepting:  () => `Hits aimed at the protected ally are redirected to this unit`,
  nature_regen:  (e, r) => `Recover <strong class="heal">${r.regenAmount ?? e.regenAmount ?? 0} Health</strong> per turn`,
  tonic:         (e, r) => `Recover <strong class="heal">${r.regenAmount ?? e.regenAmount ?? 0} Health</strong> per turn`,
  soulstitch:    () => `Recover a percentage of damage dealt as <strong class="heal">Health</strong>`,
  fury:          () => `Up to <strong class="buff">50% more Damage</strong> based on missing Health`,
  defy:          () => `Gain <strong class="buff">1 Fervor</strong> when struck`,
  catalyze:      () => `Next ability is a <strong class="buff">guaranteed Critical Strike</strong> with bonus <strong class="buff">Critical Strike Multiplier</strong>, then consumed`,
  acuity:        (_e, r) => {
    const cc = r.potency ?? 0;
    const cm = r.bonusCritMulti;
    return cm && cm > 0
      ? `Allies have <strong class="buff">${cc}% increased Critical Strike Chance</strong> and <strong class="buff">+${cm}% to Critical Strike Multiplier</strong>`
      : `Allies have <strong class="buff">${cc}% increased Critical Strike Chance</strong>`;
  },
  intensify:     () => `Allies' Fervor-generating abilities grant <strong class="buff">+1 extra Fervor</strong>`,
  venomcoat:     () => `Next attack inflicts <strong class="debuff">Poison</strong>, then consumed`,
  raised_shield: () => `Next hit against you deals <strong class="buff">40% reduced Damage</strong>, then consumed`,
  vanish:        (_e, r) => `Cannot be targeted; next ability deals <strong class="buff">${r.potency ?? 15}% more Damage</strong>, then consumed`,
  poised:        () => `Counters incoming attacks. Each counter deals <strong class="damage">Finesse Damage</strong> and applies <strong class="buff">1 Tempo</strong> to the attacker.`,

  // ── Debuffs (stat modifiers) ──
  spd_down:         (e, r) => `<strong class="debuff">${pctFromMult(r.multiplier ?? e.multiplier, 33)}% reduced Speed</strong>`,
  paralyzed:        (e, r) => `<strong class="debuff">${pctFromMult(r.multiplier ?? e.multiplier, 33)}% reduced Speed</strong>`,
  // Drowning: stackable pure slow. multiplier is per-stack (0.85 default), compounds
  // multiplicatively. Show CURRENT total slow given the active stackCount.
  drowning: (e, r) => {
    const perStack = r.multiplier ?? e.multiplier ?? 0.85;
    const stacks = r.stackCount ?? 1;
    const totalMult = Math.pow(perStack, stacks);
    const slowPct = Math.round((1 - totalMult) * 100);
    return `<strong class="debuff">${slowPct}% reduced Speed</strong>`;
  },
  // Poison: stackable. dotDamage is per-stack; show CURRENT total tick given the active stackCount.
  poison: (e, r) => {
    const stacks = r.stackCount ?? 1;
    const perStack = r.dotDamage ?? e.dotDamage ?? 0;
    const dotPerTick = perStack * stacks;
    return `<strong class="debuff">${dotPerTick} Nature Damage</strong> per turn`;
  },
  weakened:         () => `<strong class="debuff">20% reduced Damage Dealt</strong>`,
  smothered:        (_e, r) => `<strong class="debuff">${(r as any).potency ?? 30}% reduced Healing Received and Lifesteal</strong>`,
  blinded:          () => `<strong class="debuff">30% chance to miss</strong>`,
  silenced:         () => `<strong class="debuff">Focus Costs doubled</strong>`,
  rooted:           () => `Cannot gain <strong class="debuff">Speed buffs</strong>; existing Haste removed`,

  // ── Debuffs (amplifiers / consumed) ──
  hunters_mark: () => `<strong class="debuff">Projectile</strong> abilities deal <strong class="damage">25% increased Damage</strong> to this target`,
  condemn:      (_e, r) => `All attacks against this target deal <strong class="damage">${(r as any).potency ?? 10}% increased Damage</strong>`,
  fractured:    () => `Next <strong>Impact</strong> ability deals <strong class="damage">50% increased Damage</strong>, then consumed`,
  exposed:      () => `Next <strong>Exploit</strong> ability deals <strong class="damage">30% increased Damage</strong>, then consumed`,
  confusion:    () => `Next ability hits a <strong class="debuff">random target</strong>, then consumed`,
  rot:          () => `Adds to <strong class="damage">Damage over Time taken</strong> on this target. Consumed by the next Damage over Time tick; the caster gains <strong class="buff">1 Fervor</strong>`,

  // ── Debuffs (timeline disruption) ──
  // Momentum push uses displayMomentum (raw / 100) to match the Momentum Cost scale.
  stagger:       (e) => `Pushed back <strong class="debuff">${displayMomentum(e.momentumPenalty ?? 0)}</strong> Momentum`,
  stilled:       (e) => `Pushed back <strong class="debuff">${displayMomentum(e.momentumPenalty ?? 0)}</strong> Momentum`,
  // Stunned's push is dynamic (set by the source ability's tempoConsumeStun). No static value here.
  stunned:       () => `Pushed back on the timeline`,
  spore_cloud:   (e) => `Pushed back <strong class="debuff">${displayMomentum(e.momentumPenalty ?? 0)}</strong> Momentum`,

  // ── Debuffs (traps) ──
  trap_powderkeg: (e) => [
    `Triggers at start of target's turn if they have <strong>Tempo</strong>`,
    `Deals <strong class="damage">Fire Damage</strong>`,
    `<strong class="damage">+${e.trapPerStackBonus ?? 5}%</strong> increased Damage per Tempo consumed`,
  ],
  trap_vise: () => [
    `Triggers at start of target's turn if they are <strong class="debuff">Broken</strong>`,
    `Deals <strong class="damage">Steel Damage</strong>`,
  ],
  sigil_of_echoes: () => [
    `Triggers when an ally Damage ability hits this enemy`,
    `Deals <strong class="damage">Astral Damage</strong>`,
  ],
  trap_lightning_rod: () => [
    `While planted, single-target abilities that hit this enemy Chain to <strong>1</strong> additional enemy at reduced Damage`,
  ],

  // ── Debuffs (lockdown) ──
  dueled: () => `The duelist hunts this unit and ignores allies until the bout ends`,

  // ── Enemy buffs ──
  str_up:         (e, r) => `<strong class="buff">${pctFromMult(r.multiplier ?? e.multiplier, 50)}% increased Damage Dealt</strong>`,
  arm_up:         () => `All Resistances increased`,
  regen:          (e, r) => `Recover <strong class="heal">${r.regenAmount ?? e.regenAmount ?? 15} Health</strong> per turn`,
  sandstorm_fury: (e, r) => `<strong class="buff">${pctFromMult(r.multiplier ?? e.multiplier, 25)}% increased Damage Dealt</strong>`,
};

/**
 * Format a status effect for a status pill tooltip — terse, mechanical, no flavor.
 * The header (rendered separately) already shows the effect name + buff/debuff
 * badge; the duration is in the "Remaining: N turn(s)" footer. This function
 * returns just the body lines.
 *
 * Lookup order:
 *   1. PILL_DISPLAY_TEMPLATES — bespoke per-effect line
 *   2. Structural — DoT / resistance / barrier / regen-only
 *   3. Fallback — template.description (audited to be flavor-free)
 */
export function formatStatusEffectMechanic(
  template: StatusEffectTemplate,
  runtime?: PillRuntime,
): string[] {
  const r = runtime ?? {};
  const lines: string[] = [];

  // 1. Bespoke registry
  const tmpl = PILL_DISPLAY_TEMPLATES[template.id];
  if (tmpl) {
    const result = tmpl(template, r);
    return Array.isArray(result) ? result : [result];
  }

  // 2. Structural branches

  const cls = template.category === 'buff' ? 'buff' : 'debuff';

  // DoT — runtime override wins
  const dot = r.dotDamage ?? template.dotDamage;
  if (dot && dot > 0) {
    const dotEl = template.dotElement ? cap(template.dotElement) : '';
    const elPart = dotEl ? `${dotEl} ` : '';
    lines.push(`<strong class="${cls}">${dot} ${elPart}Damage</strong> per turn`);
    return lines;
  }

  // Resistance modifiers — group by value
  if (template.resistanceModifiers) {
    const entries = Object.entries(template.resistanceModifiers);
    const byVal = new Map<number, string[]>();
    for (const [el, val] of entries) {
      if (!byVal.has(val)) byVal.set(val, []);
      byVal.get(val)!.push(cap(el));
    }
    for (const [val, elements] of byVal) {
      const elLabel = elements.length >= 10
        ? 'all'
        : elements.length <= 2
          ? elements.join(' and ')
          : elements.slice(0, -1).join(', ') + ', and ' + elements[elements.length - 1];
      const sign = val >= 0 ? '+' : '';
      const valCls = val >= 0 ? 'buff' : 'debuff';
      lines.push(`<strong class="${valCls}">${sign}${val}</strong> to ${elLabel} Resistance`);
    }
    return lines;
  }

  // Barrier-granting buffs — generic line + any secondary effects
  if (template.grantsBarrier) {
    lines.push(`Absorbs incoming damage`);
    if (template.barrierEffects) lines.push(...template.barrierEffects);
    return lines;
  }

  // Regen-only effects
  const regen = r.regenAmount ?? template.regenAmount;
  if (regen && regen > 0) {
    lines.push(`Recover <strong class="heal">${regen} Health</strong> per turn`);
    return lines;
  }

  // 3. Fallback to static description
  if (template.description) {
    lines.push(template.description);
  }
  return lines;
}

/**
 * Build the minion-description block for a summon ability.
 * Format follows PoE-style "what it has / what it does / what limits it".
 */
function formatSummonLines(
  ability: AbilityConfig,
  config?: EffectLookup,
  mechCtx?: AbilityMechanicContext,
): string[] {
  const lines: string[] = [];
  const template = config?.minionTemplates?.[ability.summonTemplateId!];
  const isTotem = (ability.tags ?? []).includes('totem');

  // Line 1: what it summons + HP. Prefer per-rank summonHp from the skill rank
  // table (raise_skeleton's summonHp scales with Necrotics rank); fall back to template HP.
  const minionName = template?.name ?? cap(ability.summonTemplateId!);
  const minionHp = mechCtx?.summonHp ?? template?.hp;
  if (minionHp !== undefined) {
    lines.push(`Summons a <strong class="buff">${minionName}</strong> with <strong>${minionHp}</strong> Health`);
  } else {
    lines.push(`Summons a <strong class="buff">${minionName}</strong>`);
  }

  // Line 2 (and follow-ups): the minion's attack(s). Surface damage range + element,
  // AoE target, and Break Power. Verb branches on totem-tag: totems "Pulse" (recurring
  // self-paced action), other summons "Use".
  if (template?.abilityIds?.length && config?.abilities) {
    const verb = isTotem ? 'Pulses' : 'Uses';
    for (const abId of template.abilityIds) {
      const ab = config.abilities[abId];
      if (!ab) continue;
      if (ab.type === 'damage' && ab.minDamage != null && ab.maxDamage != null) {
        const elLabel = cap(ab.element);
        const targetSuffix = ab.target === 'all_enemies' ? ' to all enemies' : '';
        lines.push(`${verb} <strong class="buff">${ab.name}</strong>, dealing <strong>${ab.minDamage} to ${ab.maxDamage}</strong> ${elLabel} Damage${targetSuffix}`);
      } else {
        lines.push(`${verb} <strong class="buff">${ab.name}</strong>`);
      }
      // Surface the minion ability's Break Power. Canonical phrasing matches the
      // player-ability line at formatAbilityMechanics so totem-pulse Break reads
      // identically to an in-hand ability that builds Break.
      if ((ab as any).breakPowerMax) {
        const lo = (ab as any).breakPowerMin ?? 0;
        const hi = (ab as any).breakPowerMax;
        lines.push(`<strong class="damage">${lo}-${hi}</strong> Break Power`);
      }
    }
  }

  // Line 3: persistence. Rank-set duration overrides the default "persists until killed".
  if (mechCtx?.summonDurationTurns !== undefined && mechCtx.summonDurationTurns > 0) {
    const t = mechCtx.summonDurationTurns;
    lines.push(`Lasts <strong>${t}</strong> ${t === 1 ? 'turn' : 'turns'}`);
  } else {
    lines.push(`Persists until killed`);
  }

  // Line 4: group cap. Default 1 if unspecified. Totems cap by the "Totem" category
  // noun so affixes like "+1 Maximum Totems" read against the same vocabulary.
  const cap_ = ability.summonGroupMax ?? 1;
  const capNoun = isTotem ? 'Totem' : minionName;
  if (cap_ === 1) {
    lines.push(`Maximum <strong>one</strong> ${capNoun} active at a time`);
  } else {
    lines.push(`Maximum <strong>${cap_}</strong> ${capNoun}s active at a time`);
  }

  return lines;
}
